import { departmentUsesBugTracker } from '../constants/departments';

/**
 * Where a notification should take you.
 *
 * Shared by the Notifications page and the top-bar bell — two copies would
 * drift, and a notification that lands on the wrong page is worse than one you
 * cannot click at all.
 *
 * Every type the backend emits is handled explicitly below. Anything genuinely
 * unroutable (a personal confirmation with nothing to open) falls through to
 * the dashboard on purpose, not by omission.
 */

/** Types that are about an expense claim. */
const EXPENSE_TYPES = new Set([
  'expense_submitted',
  'expense_approved',
  'expense_rejected',
  'expense_escalated',
  'expense_proof_required',
  'expense_reconciliation',
]);

/** Types that are about a budget submission. */
const BUDGET_TYPES = new Set([
  'budget_submitted',
  'budget_approved',
  'budget_rejected',
]);

/** Types about someone joining — reviewed in user management. */
const TEAM_TYPES = new Set(['join_requested', 'team_joined']);

/** Types about leave. */
const LEAVE_TYPES = new Set(['leave_applied', 'leave_approved']);

/**
 * Confirmations addressed to the reader about their own account. There is no
 * detail page for "you were approved" — the dashboard is the right landing.
 */
const SELF_TYPES = new Set(['approved', 'onboarding']);

/** Tracker's state tabs — mirrors STATE_TABS in constants/trackerUi.js. */
const TRACKER_STATES = new Set(['active', 'backlog', 'on_hold', 'completed']);

export function routeForNotification(notification, user) {
  const n = notification || {};
  const type = String(n.notification_type || '');

  // entity_id is what the alert is about when it is not an issue. Older rows
  // predate the column, so every branch works without it too.
  const entityId = n.entity_id ?? null;

  /**
   * Tracker alerts open the pipeline whether or not a story exists yet — a
   * stage handed over has no story until its owner accepts it.
   *
   * With a requirement id, go straight to it: Tracker reads ?requirement= and
   * scrolls that card into view, opened.
   *
   * The tab matters as much as the id: land on the wrong one and the list is
   * empty, hiding the very thing the alert was about. Use the requirement's
   * own state, which is what Tracker's tabs are keyed on — never a guess from
   * the alert type. "Requirement is yours" arrives while the requirement is
   * still in Backlog (nothing has started until the owner accepts), so
   * inferring "assigned means active" sends people to an empty Active tab.
   */
  if (type.startsWith('tracker_')) {
    if (!entityId) return '/tracker';
    const params = new URLSearchParams({ requirement: String(entityId) });
    // Only pass a tab we know Tracker has; anything else it would ignore.
    if (TRACKER_STATES.has(n.requirement_state)) {
      params.set('state', n.requirement_state);
    }
    // Tracker filters by project, so without this it would open whichever
    // project happens to be first and the card would not be in the list.
    if (n.requirement_project_id) params.set('project', n.requirement_project_id);
    return `/tracker?${params.toString()}`;
  }

  if (EXPENSE_TYPES.has(type)) {
    // Approvers land on the queue they act from; claimants on their own list.
    const tab =
      type === 'expense_submitted'
        ? 'hod_queue'
        : type === 'expense_escalated'
          ? 'escalated'
          : 'mine';
    const params = new URLSearchParams({ tab });
    if (entityId) params.set('claimId', String(entityId));
    return `/expense-claims?${params.toString()}`;
  }

  if (BUDGET_TYPES.has(type)) {
    return entityId ? `/budget?submissionId=${entityId}` : '/budget';
  }

  if (TEAM_TYPES.has(type)) {
    return '/users-management';
  }

  if (LEAVE_TYPES.has(type)) {
    return '/team-leave';
  }

  if (SELF_TYPES.has(type)) {
    return '/dashboard';
  }

  // Issue-backed alerts: tagged, commented, assignment.
  if (n.issue_id) {
    if (n.issuetype === 'Bug' && departmentUsesBugTracker(user?.department)) {
      return `/issues?openIssueId=${n.issue_id}`;
    }
    return `/sprint-board?openIssueId=${n.issue_id}`;
  }

  return '/dashboard';
}

export default routeForNotification;
